import { useMemo, useState } from "react";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";
import { action } from "@k8slens/lds-icons";
import { BusinessInvitation, type BusinessUser } from "lens-platform-sdk";

import { useAnalytics } from "src/hooks/useAnalytics";
import { BusinessAdminInvitation } from "src/hooks/useGetBusinessInvitations";
import { getUserAvatar } from "src/utils/user";

import Table from "src/components/Table/Table";
import { TabList, TabGroup, Tab, TabPanel, TabPanels } from "src/components/Tabs/Tabs";
import { ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import { getUserFullname } from "src/services/getUserFullname";
import { AvatarTableCell, PendingUserTableCell } from "src/components/TableCells/AvatarTableCell";
import { dateColDef } from "src/components/TableCells/DateCell";
import ContextMenuCell, { contextMenuColDef } from "src/components/TableCells/ContextMenuCell";

import styles from "./Administrators.module.css";

const columnHelper = createColumnHelper<BusinessUser>();
const invitationColumnHelper = createColumnHelper<BusinessInvitation>();
const { DeleteIcon } = action;

const columns = [
  columnHelper.accessor("username", {
    header: () => <span>Lens ID</span>,
    cell: ({ getValue, row }) => {
      const username = getValue();
      const user = row.original;
      const { email } = user;
      const userAvatar = getUserAvatar(user);

      // User is invited to the business, but not yet have a Lens ID account
      const userNotHaveAccount = email && !username;

      if (userNotHaveAccount) {
        return <PendingUserTableCell email={email} />;
      }

      const fullname = getUserFullname(user);

      return <AvatarTableCell image={userAvatar} name={username ?? ""} subtitle={fullname} />;
    },
    meta: { primary: true },
  }),
];

type Props = {
  businessAdminInvitations: BusinessAdminInvitation[];
  businessAdministrators: BusinessUser[];
  selectedTableTabIndex?: number;
  setSelectedTableTabIndex: (index: number) => void;
  onRemoveBusinessUser: (userId: string, businessUserId: string) => Promise<void>;
  onRemoveBusinessInvitation: (id: string) => Promise<void>;
};

/**
 * The 'Administrators' table to be used on the `business/:businessId/account` page
 */
export const Administrators = ({
  businessAdministrators,
  selectedTableTabIndex,
  setSelectedTableTabIndex,
  onRemoveBusinessUser,
  onRemoveBusinessInvitation,
  businessAdminInvitations,
}: Props) => {
  const { trackButtonClicked } = useAnalytics();
  const numberOfActiveAdministrators = useMemo(
    () => businessAdministrators.filter(({ state }) => state === "active").length,
    [businessAdministrators],
  );
  const numberOfPendingAdministrators = useMemo(
    () => businessAdminInvitations.filter(({ state }) => state === "pending").length,
    [businessAdminInvitations],
  );

  const activeAdministrators = useMemo<BusinessUser[]>(
    () => businessAdministrators.filter(({ state }) => state === "active"),
    [businessAdministrators],
  );
  const [activeAdminSorting, setActiveAdminSorting] = useState<SortingState>([]);
  const [pendingAdminSorting, setPendingAdminSorting] = useState<SortingState>([]);
  const activeAdminColumns = useMemo(
    () => [
      ...columns,
      columnHelper.accessor("businessUserId", {
        ...contextMenuColDef,
        cell: ({ row }) => {
          const user = row.original;

          return (
            <ContextMenuCell toggleButtonProps={{ title: "Toggle context menu" }}>
              <ContextMenuItem
                label="Remove"
                icon={DeleteIcon}
                onClick={() => user?.id && user?.businessUserId && onRemoveBusinessUser(user.id, user.businessUserId)}
              />
            </ContextMenuCell>
          );
        },
      }),
    ],
    [onRemoveBusinessUser],
  );

  const pendingAdminColumns = useMemo(
    () => [
      invitationColumnHelper.accessor("email", {
        header: () => <span>Email</span>,
        cell: ({ getValue }) => <PendingUserTableCell email={getValue()} />,
        meta: { primary: true },
      }),
      invitationColumnHelper.accessor("createdAt", {
        ...dateColDef,
        header: () => <span>Invitation Date</span>,
      }),
      invitationColumnHelper.accessor("id", {
        ...contextMenuColDef,
        cell: ({ getValue }) => (
          <ContextMenuCell toggleButtonProps={{ title: "Toggle context menu" }}>
            <ContextMenuItem label="Remove" icon={DeleteIcon} onClick={() => onRemoveBusinessInvitation(getValue())} />
          </ContextMenuCell>
        ),
      }),
    ],
    [onRemoveBusinessInvitation],
  );

  const activeAdminTable = useReactTable({
    data: activeAdministrators,
    columns: activeAdminColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting: activeAdminSorting,
    },
    onSortingChange: setActiveAdminSorting,
  });
  const activeAdminRowModel = activeAdminTable.getRowModel();
  const activeAdminRows = useMemo(() => activeAdminRowModel.rows, [activeAdminRowModel.rows]);
  const pendinAdminTable = useReactTable({
    data: businessAdminInvitations,
    columns: pendingAdminColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting: pendingAdminSorting,
    },
    onSortingChange: setPendingAdminSorting,
  });
  const pendinAdminRowModel = pendinAdminTable.getRowModel();
  const pendinAdminRows = useMemo(() => pendinAdminRowModel.rows, [pendinAdminRowModel.rows]);

  const tabs = useMemo(
    () => [
      { key: "active", label: `Active (${numberOfActiveAdministrators})` },
      { key: "pending", label: `Pending (${numberOfPendingAdministrators})` },
    ],
    [numberOfActiveAdministrators, numberOfPendingAdministrators],
  );

  const handleChange = (index: number) => {
    setSelectedTableTabIndex(index);
    trackButtonClicked(tabs[index]!.label, { type: "tab" });
  };

  return (
    <TabGroup onChange={handleChange} selectedIndex={selectedTableTabIndex}>
      <div className={styles.tableTab}>
        <span className={styles.tableSubTitle}>
          Invite admins to this business in here. Admins have access to configure and manage this Lens Business ID.
        </span>
        <TabList className={styles.tableTabButtonBar}>
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </TabList>
      </div>
      <TabPanels>
        <TabPanel>
          <Table<BusinessUser>
            caption="Active administrators"
            responsive={false}
            rows={activeAdminRows}
            columns={activeAdminTable.getAllColumns()}
            headerGroups={activeAdminTable.getHeaderGroups()}
            noDataText="No active administrators!"
          />
        </TabPanel>
        <TabPanel>
          <Table<BusinessInvitation>
            caption="Pending administrators"
            responsive={false}
            rows={pendinAdminRows}
            columns={pendinAdminTable.getAllColumns()}
            headerGroups={pendinAdminTable.getHeaderGroups()}
            noDataText="No pending administrators!"
          />
        </TabPanel>
      </TabPanels>
    </TabGroup>
  );
};
